import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { useFileUpload } from "lib/hooks/useFileUpload.jsx";
import { postUdFileUpload } from "lib/api/postUdFileUpload.js";
import { useAppStore } from "lib/store/useAppStore.js";
import UploadWorkspace from "./components/UploadWorkspace";
import HelpTip from "components/HelpTip";
import Spinner from "components/Spinner";
import { getProjectNodeQr } from "lib/api/getFacilityQr";
import { readNodeQrInput } from "lib/utils/facilityQr";

const MAX_UPLOAD_FILE_COUNT = 100;
const EMPTY_FILE_GROUPS = [null, null, null, null, null, null];
const METADATA_PREFIX_GROUP_INDEX = {
  T: 0,
  B: 1,
  F: 2,
  L: 3,
  N: 4,
  P: 5,
};
const PROJECT_ROADMAP_UNASSIGNED_VALUE = "__unassigned__";

function normalizeUploadMetadataValue(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === PROJECT_ROADMAP_UNASSIGNED_VALUE) {
    return null;
  }
  return normalized;
}

function normalizeUploadMetadataGroups(groups) {
  return EMPTY_FILE_GROUPS.map((_, index) =>
    normalizeUploadMetadataValue(groups?.[index]),
  );
}

export function applyUploadMetadataDefaults(detectedGroups, defaultGroups) {
  const normalizedDetectedGroups =
    normalizeUploadMetadataGroups(detectedGroups);
  const normalizedDefaultGroups = normalizeUploadMetadataGroups(defaultGroups);
  const defaultApplied = normalizedDetectedGroups.map(
    (detectedValue, index) =>
      !detectedValue && Boolean(normalizedDefaultGroups[index]),
  );

  return {
    detectedGroups: normalizedDetectedGroups,
    defaultApplied,
    groups: normalizedDetectedGroups.map(
      (detectedValue, index) =>
        detectedValue ?? normalizedDefaultGroups[index] ?? null,
    ),
  };
}

export function reapplyUploadMetadataDefaults(fileData, defaultGroups) {
  // A node QR defines its complete path, including intentionally empty levels.
  if (fileData?.node_id) return fileData;
  const normalizedDefaultGroups = normalizeUploadMetadataGroups(defaultGroups);
  const detectedGroups = normalizeUploadMetadataGroups(
    fileData?.detectedGroups,
  );
  const currentGroups = normalizeUploadMetadataGroups(fileData?.groups);
  const previousDefaultApplied = EMPTY_FILE_GROUPS.map((_, index) =>
    Boolean(fileData?.defaultApplied?.[index]),
  );

  const groups = currentGroups.map((currentValue, index) => {
    if (previousDefaultApplied[index]) {
      return detectedGroups[index] ?? normalizedDefaultGroups[index] ?? null;
    }
    if (!currentValue) {
      return detectedGroups[index] ?? normalizedDefaultGroups[index] ?? null;
    }
    return currentValue;
  });
  const defaultApplied = groups.map(
    (value, index) =>
      !detectedGroups[index] &&
      Boolean(normalizedDefaultGroups[index]) &&
      value === normalizedDefaultGroups[index] &&
      (previousDefaultApplied[index] || !currentGroups[index]),
  );

  return {
    ...fileData,
    detectedGroups,
    defaultApplied,
    groups,
  };
}

function getUniqueMetadataValues(files, fieldName) {
  return Array.from(
    new Set(
      files
        .map((file) => normalizeUploadMetadataValue(file?.[fieldName]))
        .filter(Boolean),
    ),
  ).sort((left, right) =>
    left.localeCompare(right, "ko", {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

export function buildProjectUploadMetadataOptions(projectTree, projectId) {
  const normalizedProjectId = String(projectId ?? "")
    .trim()
    .toLowerCase();
  const project = (Array.isArray(projectTree) ? projectTree : []).find(
    (item) =>
      String(item?.project_id ?? "")
        .trim()
        .toLowerCase() === normalizedProjectId,
  );
  const files = Array.isArray(project?.files) ? project.files : [];
  const nodes = Array.isArray(project?.nodes) ? project.nodes : [];
  const registeredValues = (type, field) => getUniqueMetadataValues(
    Array.isArray(project?.nodes)
      ? nodes.filter((node) => node.node_type === type).map((node) => ({ [field]: node.name }))
      : files,
    field,
  );

  return {
    facilityTypes: getUniqueMetadataValues(files, "facility_type"),
    buildingNames: registeredValues("building", "building_name"),
    floors: registeredValues("floor", "floor"),
    locations: registeredValues("location", "location"),
    facilityNames: registeredValues("facility", "facility_name"),
    partNames: registeredValues("part", "part_name"),
  };
}

function getFileNameWithoutExtension(fileName) {
  const normalizedName = String(fileName ?? "").trim();
  const extensionIndex = normalizedName.lastIndexOf(".");
  return extensionIndex > 0
    ? normalizedName.slice(0, extensionIndex)
    : normalizedName;
}

function getPrefixedFolderMetadata(name) {
  const normalizedName = String(name ?? "").trim();
  const matches = Array.from(normalizedName.matchAll(/(NODE|QR|[TBFLNP])_/gi))
    .filter((match) => match.index === 0 || match[1] === match[1].toUpperCase() ||
      /\s/.test(normalizedName[match.index - 1]));
  if (!matches.length || matches[0].index !== 0) return {};

  const metadata = {};
  let current = matches[0];
  const saveValue = (end) => {
    const value = normalizedName.slice(current.index + current[0].length, end).trim();
    if (value) metadata[current[1].toUpperCase()] = value;
  };
  for (const next of matches.slice(1)) {
    if (["QR", "NODE"].includes(current[1].toUpperCase())) {
      const start = current.index + current[0].length;
      const token = normalizedName.slice(start).match(/^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[A-Za-z0-9_-]{22})/i)?.[0];
      if (token && readNodeQrInput(token) && next.index < start + token.length) continue;
    }
    // Existing custom QR IDs can themselves start with a prefix (e.g. T_ab_cd).
    if (current[1].toUpperCase() === "QR" &&
        !normalizedName.slice(current.index + current[0].length, next.index).trim()) {
      continue;
    }
    saveValue(next.index);
    current = next;
  }
  saveValue(normalizedName.length);
  return metadata;
}

function getPrefixedFileGroups(name) {
  const groups = [...EMPTY_FILE_GROUPS];
  for (const [prefix, value] of Object.entries(getPrefixedFolderMetadata(name))) {
    const groupIndex = METADATA_PREFIX_GROUP_INDEX[prefix];
    if (groupIndex != null) groups[groupIndex] = value;
  }
  return groups;
}

function mergeFileGroups(baseGroups, overridingGroups) {
  return EMPTY_FILE_GROUPS.map(
    (_, index) => overridingGroups[index] ?? baseGroups[index] ?? null,
  );
}

export function getArchiveEntryFileGroups(archiveFileName, entryName) {
  let groups = getPrefixedFileGroups(
    getFileNameWithoutExtension(archiveFileName),
  );
  const pathParts = String(entryName ?? "")
    .split(/[\\/]/)
    .filter(Boolean);
  const directoryParts = pathParts.slice(0, -1);

  directoryParts.forEach((directoryName) => {
    groups = mergeFileGroups(groups, getPrefixedFileGroups(directoryName));
  });

  return groups;
}

export function getArchiveEntryQrId(archiveFileName, entryName, prefix = "QR") {
  const directoryParts = String(entryName ?? "")
    .split(/[\\/]/)
    .filter(Boolean)
    .slice(0, -1);
  let qrId = null;
  for (const name of [getFileNameWithoutExtension(archiveFileName), ...directoryParts]) {
    qrId = getPrefixedFolderMetadata(name)[prefix] ?? qrId;
  }
  return qrId;
}

function getArchiveEntryFileName(entryName) {
  return (
    String(entryName ?? "")
      .split(/[\\/]/)
      .filter(Boolean)
      .pop() ?? ""
  );
}

function isSupportedUploadFile(file) {
  const lowerFileName = String(file?.name ?? "").toLowerCase();
  return lowerFileName.endsWith(".ud") || lowerFileName.endsWith(".zip");
}

function getSelectedFileRelativePath(file) {
  return String(
    file?.webkitRelativePath || file?.uploadRelativePath || file?.name || "",
  ).trim();
}

function createFileWithRelativePath(file, relativePath) {
  const nextFile = new File([file], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
  Object.defineProperty(nextFile, "uploadRelativePath", {
    configurable: false,
    enumerable: false,
    value: relativePath,
    writable: false,
  });
  return nextFile;
}

function readDroppedFileEntry(fileEntry, relativePath) {
  return new Promise((resolve, reject) => {
    fileEntry.file(
      (file) => {
        resolve(
          isSupportedUploadFile(file)
            ? [createFileWithRelativePath(file, relativePath)]
            : [],
        );
      },
      (error) => reject(error),
    );
  });
}

async function readAllDirectoryEntries(directoryEntry) {
  const reader = directoryEntry.createReader();
  const entries = [];

  while (true) {
    const batch = await new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (!batch.length) return entries;
    entries.push(...batch);
  }
}

async function collectDroppedEntryFiles(entry, parentPath = "") {
  if (!entry) return [];

  const relativePath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  if (entry.isFile) {
    return readDroppedFileEntry(entry, relativePath);
  }
  if (!entry.isDirectory) return [];

  const childEntries = await readAllDirectoryEntries(entry);
  const nestedFiles = await Promise.all(
    childEntries.map((childEntry) =>
      collectDroppedEntryFiles(childEntry, relativePath),
    ),
  );
  return nestedFiles.flat();
}

function createUniqueUploadKey(fileName, usedKeys) {
  const baseKey = getFileNameWithoutExtension(fileName) || "file";
  let nextKey = baseKey;
  let suffix = 2;

  while (usedKeys.has(nextKey)) {
    nextKey = `${baseKey} (${suffix})`;
    suffix += 1;
  }

  usedKeys.add(nextKey);
  return nextKey;
}

export default function ProjectFileUploadPage() {
  const fetchProjectTree = useAppStore((s) => s.fetchProjectTree);
  const user = useAppStore((s) => s.user);
  const {
    id = null,
    last_selected_project = null,
    last_selected_project_name = null,
  } = user ?? {};
  const showAlert = useAppStore((s) => s.showAlert);

  return (
    <ProjectFileUploadPanel
      className="content"
      projectId={last_selected_project}
      projectName={last_selected_project_name}
      userId={id}
      fetchProjectTree={fetchProjectTree}
      showAlert={showAlert}
    />
  );
}

export function ProjectFileUploadPanel({
  className = "",
  projectId = null,
  projectName = "-",
  userId = null,
  defaultMetadata = {},
  fetchProjectTree,
  showAlert,
  onUploadSuccess,
  onFilesDrag,
  showTitle = true,
}) {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.resolvedLanguage ?? i18n.language ?? "";
  const inputId = useId();
  const notify = showAlert ?? (() => {});
  const projectTree = useAppStore((state) => state.projectTree);
  const defaultFacilityType = normalizeUploadMetadataValue(
    defaultMetadata?.facilityType,
  );
  const defaultBuildingName = normalizeUploadMetadataValue(
    defaultMetadata?.buildingName,
  );
  const defaultFloor = normalizeUploadMetadataValue(defaultMetadata?.floor);
  const defaultLocation = normalizeUploadMetadataValue(
    defaultMetadata?.location,
  );
  const defaultFacilityName = normalizeUploadMetadataValue(
    defaultMetadata?.facilityName,
  );
  const defaultPartName = normalizeUploadMetadataValue(defaultMetadata?.partName);
  const incomingDefaultGroups = useMemo(
    () => [
      defaultFacilityType,
      defaultBuildingName,
      defaultFloor,
      defaultLocation,
      defaultFacilityName,
      defaultPartName,
    ],
    [
      defaultBuildingName,
      defaultFacilityName,
      defaultFacilityType,
      defaultFloor,
      defaultLocation,
      defaultPartName,
    ],
  );
  const [activeDefaultGroups, setActiveDefaultGroups] = useState(
    () => incomingDefaultGroups,
  );
  const metadataOptions = useMemo(
    () => buildProjectUploadMetadataOptions(projectTree, projectId),
    [projectId, projectTree],
  );
  const registeredNodes = useMemo(() => (projectTree ?? []).find(project =>
    String(project.project_id).toLowerCase() === String(projectId).toLowerCase())?.nodes ?? [], [projectTree, projectId]);

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const {
    uploadedFiles,
    setUploadedFiles,
    thumbnails,
    setThumbnails,
    faildList,
    setFaildList,
  } = useFileUpload(setIsUploadLoading);

  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const rootClassName = ["fileUpload", className].filter(Boolean).join(" ");

  useEffect(() => {
    setActiveDefaultGroups(incomingDefaultGroups);
  }, [incomingDefaultGroups, projectId]);

  useEffect(() => {
    if (!userId || !projectId || typeof fetchProjectTree !== "function") {
      return;
    }

    Promise.resolve(fetchProjectTree(userId, projectId)).catch((error) => {
      console.warn("[UPLOAD-METADATA-OPTIONS-LOAD-ERROR]", error);
    });
  }, [fetchProjectTree, projectId, userId]);

  const resetMetadataDefaults = useCallback(() => {
    setActiveDefaultGroups([...EMPTY_FILE_GROUPS]);
  }, []);

  const orderedThumbnails = useMemo(() => {
    const arr = Array.isArray(thumbnails) ? thumbnails : [];
    return [...arr].sort((a, b) => Number(a?.name ?? 0) - Number(b?.name ?? 0));
  }, [thumbnails]);

  return (
    <section className={rootClassName}>
      {showTitle && (
        <div className="helpTitleRow">
          <h2>
            {t("projectDetail.fileUpload.title", {
              projectName: projectName ?? "-",
            })}
          </h2>
          <HelpTip tipKey="FILE_UPLOAD" />
        </div>
      )}

      <FailedUploadedFileList faildList={faildList} t={t} />

      <UploadWorkspace
        key={projectId}
        filePicker={
        <InputFile
          onFilesDrag={onFilesDrag}
          inputId={inputId}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          setFaildList={setFaildList}
          setIsUploadLoading={setIsUploadLoading}
          project_id={projectId}
          showAlert={notify}
          t={t}
          defaultGroups={activeDefaultGroups}
          nodes={registeredNodes}
        />
        }
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        setThumbnails={setThumbnails}
        thumbnails={orderedThumbnails}
        nodes={registeredNodes}
        metadataOptions={metadataOptions}
        onSelectTree={groups => setActiveDefaultGroups(current => [current[0], ...groups.slice(1)])}
        actions={<>
          <ResetBtn setUploadedFiles={setUploadedFiles} setThumbnails={setThumbnails} setFaildList={setFaildList} onReset={resetMetadataDefaults} t={t} />
          <FileUploadBtn project_id={projectId} user_id={userId} uploadedFiles={uploadedFiles} isSaveLoading={isSaveLoading}
            setIsSaveLoading={setIsSaveLoading} setUploadedFiles={setUploadedFiles} fetchProjectTree={fetchProjectTree}
            setThumbnails={setThumbnails} showAlert={notify} onUploadSuccess={onUploadSuccess} t={t} locale={currentLocale} />
        </>}
      />

      {(isSaveLoading || isUploadLoading) && <Spinner type="file_upload" />}
    </section>
  );
}

export function InputFile({
  onFilesDrag,
  nodes = [],
  inputId = "fileInput",
  uploadedFiles,
  setUploadedFiles,
  setFaildList,
  setIsUploadLoading,
  project_id,
  showAlert,
  t,
  defaultGroups = EMPTY_FILE_GROUPS,
}) {
  const [nodeQrInput] = useState(() => new URLSearchParams(window.location.search).get("treeNodeId") || "");
  const [nodeQrError, setNodeQrError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const pickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const isProcessingFiles = useRef(false);
  const folderInputId = `${inputId}-folder`;

  const showUploadAlert = (textKey) => {
    showAlert({
      textKey,
      text: t(textKey),
      success: false,
      autoClose: true,
    });
  };

  const addArchiveFailure = (archiveFile, reasonKey) => {
    if (typeof setFaildList !== "function") return;

    const failureKey = [
      "archive",
      archiveFile?.name,
      archiveFile?.lastModified,
    ].join(":");

    setFaildList((prev) => ({
      ...(prev ?? {}),
      [failureKey]: {
        fileName: archiveFile?.name ?? "",
        reasonKey,
      },
    }));
  };

  const processFiles = async (fileList) => {
    if (isProcessingFiles.current) return;

    const uploadedKeys = Object.keys(uploadedFiles ?? {}).length;

    if (!project_id) {
      setUploadedFiles({});
      showUploadAlert("projectDetail.fileUpload.alerts.selectProject");
      return;
    }

    if (!fileList || fileList.length === 0) {
      return;
    }

    const selectedFiles = Array.from(fileList);
    const supportedFiles = selectedFiles.filter(isSupportedUploadFile);

    if (supportedFiles.length === 0) {
      showUploadAlert("projectDetail.fileUpload.alerts.onlyUd");
      return;
    }

    isProcessingFiles.current = true;
    setNodeQrError("");
    setIsUploadLoading?.(true);

    try {
      const nextFiles = [];

      for (const selectedFile of supportedFiles) {
        if (selectedFile.name.toLowerCase().endsWith(".ud")) {
          const detectedGroups = getArchiveEntryFileGroups(
            "",
            getSelectedFileRelativePath(selectedFile),
          );
          nextFiles.push({
            file: selectedFile,
            ...applyUploadMetadataDefaults(detectedGroups, defaultGroups),
            qr_id: getArchiveEntryQrId("", getSelectedFileRelativePath(selectedFile)),
            node_id: getArchiveEntryQrId("", getSelectedFileRelativePath(selectedFile), "NODE"),
            folderPath: getSelectedFileRelativePath(selectedFile),
          });
          continue;
        }

        let archive;
        try {
          archive = await JSZip.loadAsync(selectedFile);
        } catch {
          addArchiveFailure(
            selectedFile,
            "projectDetail.fileUpload.failedList.reasonUnreadableZip",
          );
          continue;
        }

        const udEntries = Object.values(archive.files).filter(
          (entry) =>
            !entry.dir &&
            getArchiveEntryFileName(entry.name).toLowerCase().endsWith(".ud"),
        );

        if (udEntries.length === 0) {
          addArchiveFailure(
            selectedFile,
            "projectDetail.fileUpload.failedList.reasonNoUdFiles",
          );
          continue;
        }

        if (
          uploadedKeys + nextFiles.length + udEntries.length >
          MAX_UPLOAD_FILE_COUNT
        ) {
          showUploadAlert("projectDetail.fileUpload.alerts.maxFiles");
          return;
        }

        for (const entry of udEntries) {
          const extractedFileName = getArchiveEntryFileName(entry.name);
          const extractedBlob = await entry.async("blob");
          const extractedFile = new File([extractedBlob], extractedFileName, {
            type: "application/octet-stream",
            lastModified: selectedFile.lastModified,
          });

          const detectedGroups = mergeFileGroups(
            getArchiveEntryFileGroups(
              "",
              getSelectedFileRelativePath(selectedFile),
            ),
            getArchiveEntryFileGroups(selectedFile.name, entry.name),
          );

          nextFiles.push({
            file: extractedFile,
            ...applyUploadMetadataDefaults(detectedGroups, defaultGroups),
            qr_id:
              getArchiveEntryQrId(selectedFile.name, entry.name) ??
              getArchiveEntryQrId("", getSelectedFileRelativePath(selectedFile)),
            archiveName: selectedFile.name,
            node_id: getArchiveEntryQrId(selectedFile.name, entry.name, "NODE") ?? getArchiveEntryQrId("", getSelectedFileRelativePath(selectedFile), "NODE"),
            archivePath: entry.name,
          });
        }
      }

      const defaultNodeId = nodeQrInput.trim() ? readNodeQrInput(nodeQrInput) : null;
      if (nodeQrInput.trim() && !defaultNodeId) throw new Error("QR 코드의 경로를 확인할 수 없습니다. QR 코드를 다시 스캔해 주세요.");
      nextFiles.forEach(item => {
        if (item.qr_id) {
          const id = readNodeQrInput(item.qr_id);
          if (!id) throw new Error("폴더의 QR ID 형식을 확인하세요.");
          item.qr_id = id;
        }
        if (item.node_id) {
          const id = readNodeQrInput(item.node_id);
          if (!id) throw new Error("폴더명에 포함된 경로 ID 형식을 확인해 주세요.");
          item.node_id = id;
        }
      });
      const nodeIds = [...new Set(nextFiles.flatMap(item => [item.qr_id, item.node_id || (!item.qr_id ? defaultNodeId : null)]).filter(Boolean))];
      const nodePaths = new Map(await Promise.all(nodeIds.map(async nodeId => {
        const result = await getProjectNodeQr(nodeId);
        if (!result.success || result.data.project_id.toLowerCase() !== project_id.toLowerCase()) throw new Error("현재 프로젝트에서 발급한 QR 코드를 사용하세요.");
        return [nodeId, { ...result.data.metadata, node_id: result.data.node_id }];
      })));
      nextFiles.forEach(item => {
        const nodeId = item.qr_id || item.node_id || defaultNodeId;
        if (!nodeId) return;
        const path = nodePaths.get(nodeId);
        if (item.qr_id && item.node_id && path.node_id.toLowerCase() !== nodePaths.get(item.node_id).node_id.toLowerCase()) throw new Error("폴더명에 포함된 QR 코드와 경로 ID가 서로 다른 위치를 가리킵니다.");
        item.node_id = path.node_id;
        item.qr_id = null;
        item.groups = [item.groups[0], path.building_name, path.floor, path.location, path.facility_name, path.part_name];
        item.detectedGroups = [...item.groups];
        item.defaultApplied = EMPTY_FILE_GROUPS.map(() => false);
      });
      if (nextFiles.length === 0) {
        showUploadAlert("projectDetail.fileUpload.alerts.onlyUd");
        return;
      }

      if (uploadedKeys + nextFiles.length > MAX_UPLOAD_FILE_COUNT) {
        showUploadAlert("projectDetail.fileUpload.alerts.maxFiles");
        return;
      }

      setUploadedFiles((prev) => {
        const updated = { ...(prev ?? {}) };
        const usedKeys = new Set(Object.keys(updated));

        nextFiles.forEach((fileData) => {
          const uploadKey = createUniqueUploadKey(fileData.file.name, usedKeys);
          updated[uploadKey] = fileData;
        });

        return updated;
      });
    } catch (error) {
      console.error("[UD-ARCHIVE-EXTRACT-ERROR]", error);
      setNodeQrError(error.message || "파일 경로를 확인하세요.");
      showUploadAlert("projectDetail.fileUpload.alerts.unknownError");
    } finally {
      isProcessingFiles.current = false;
      setIsUploadLoading?.(false);
    }
  };

  const handleChangeInput = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    e.target.value = "";
    processFiles(files);
  };

  const handleChangeFolderInput = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    e.target.value = "";
    processFiles(files);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Capture files and directory entries while the native drop event is active.
    const files = Array.from(e.dataTransfer?.files ?? []);

    const droppedEntries = Array.from(e.dataTransfer?.items ?? [])
      .map((item) => item.webkitGetAsEntry?.())
      .filter(Boolean);
    if (droppedEntries.length > 0) {
      try {
        const nestedFiles = (
          await Promise.all(
            droppedEntries.map((entry) => collectDroppedEntryFiles(entry)),
          )
        ).flat();
        if (nestedFiles.length === 0) {
          showUploadAlert("projectDetail.fileUpload.alerts.onlyUd");
          return;
        }
        await processFiles(nestedFiles);
        return;
      } catch (error) {
        console.error("[UD-FOLDER-DROP-ERROR]", error);
      }
    }

    if (files.length) {
      await processFiles(files);
    }
  };

  useEffect(() => {
    const scope = pickerRef.current?.closest(".projectDetail.content") || pickerRef.current?.closest(".fileUpload");
    if (!scope) return;
    const isFileDrag = event => Array.from(event.dataTransfer?.types ?? []).includes("Files");
    const enter = event => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      onFilesDrag?.();
      setIsDragging(true);
    };
    const over = event => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    };
    const leave = event => {
      if (!scope.contains(event.relatedTarget)) setIsDragging(false);
    };
    const drop = event => {
      if (!isFileDrag(event)) return;
      onFilesDrag?.();
      void onDrop(event);
    };
    const reset = () => setIsDragging(false);
    scope.addEventListener("dragenter", enter, true);
    scope.addEventListener("dragover", over, true);
    scope.addEventListener("dragleave", leave, true);
    scope.addEventListener("drop", drop, true);
    window.addEventListener("dragend", reset);
    window.addEventListener("blur", reset);
    return () => {
      scope.removeEventListener("dragenter", enter, true);
      scope.removeEventListener("dragover", over, true);
      scope.removeEventListener("dragleave", leave, true);
      scope.removeEventListener("drop", drop, true);
      window.removeEventListener("dragend", reset);
      window.removeEventListener("blur", reset);
    };
  });

  return (
    <div className="uploadWorkspace__filePicker" ref={pickerRef}>
      <div className="uploadWorkspace__pickerActions">
        <button type="button" className="btn-bg-blue" onClick={() => fileInputRef.current?.click()}>파일 추가</button>
        <button type="button" className="btn-bg-blue" onClick={() => folderInputRef.current?.click()}>{t("projectDetail.fileUpload.browseFolderButton")}</button>
      </div>
      <p role="status">{isDragging ? "파일이나 폴더를 놓으면 업로드 목록에 추가합니다." : "파일·폴더를 화면에 끌어 놓거나 버튼으로 추가하세요. (UD, ZIP)"}</p>
      {nodeQrError && <p role="alert">{nodeQrError}</p>}
      <input
        hidden
        ref={fileInputRef}
        title={t("projectDetail.fileUpload.dropzoneTitle")}
        onChange={handleChangeInput}
        type="file"
        accept=".ud,.zip"
        id={inputId}
        multiple
      />
      <input
        hidden
        ref={folderInputRef}
        directory=""
        id={folderInputId}
        multiple
        onChange={handleChangeFolderInput}
        title={t("projectDetail.fileUpload.browseFolderButton")}
        type="file"
        webkitdirectory=""
      />
    </div>
  );
}

export function FailedUploadedFileList({ faildList, t }) {
  if (!faildList || Object.keys(faildList).length === 0) return null;

  const getFileTypeLabel = (fileType) => {
    if (fileType === "image") {
      return t("projectDetail.fileUpload.uploadedList.fileTypeImage");
    }
    if (fileType === "video") {
      return t("projectDetail.fileUpload.uploadedList.fileTypeVideo");
    }
    return "";
  };

  const renderFileName = (item) => {
    const baseName = item?.fileName ?? item?.filename ?? "";
    if (!baseName) return "";

    const typeLabel = getFileTypeLabel(item?.fileType);
    return typeLabel ? `${baseName} (${typeLabel})` : baseName;
  };

  const renderReason = (item) => {
    if (item?.reasonKey) {
      return t(item.reasonKey, item.reasonParams ?? {});
    }
    return item?.reason ?? "";
  };

  return (
    <table className="failedUploadedFileList">
      <caption>{t("projectDetail.fileUpload.failedList.caption")}</caption>
      <thead>
        <tr>
          <th>{t("projectDetail.fileUpload.failedList.fileNameType")}</th>
          <th>{t("projectDetail.fileUpload.failedList.reason")}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(faildList).map(([key, item]) => (
          <tr key={key}>
            <td>{renderFileName(item)}</td>
            <td>{renderReason(item)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ResetBtn({
  setUploadedFiles,
  setThumbnails,
  setFaildList,
  onReset,
  t,
}) {
  const handleResetInput = () => {
    setUploadedFiles({});
    setThumbnails([]);
    setFaildList({});
    onReset?.();
  };

  return (
    <button
      type="button"
      onClick={handleResetInput}
      title={t("projectDetail.fileUpload.buttons.reset")}
      className="btn-bg-blue"
    >
      {t("projectDetail.fileUpload.buttons.reset")}
    </button>
  );
}

export function FileUploadBtn({
  project_id,
  uploadedFiles,
  user_id,
  isSaveLoading,
  setIsSaveLoading,
  setUploadedFiles,
  fetchProjectTree,
  setThumbnails,
  showAlert,
  onUploadSuccess,
  t,
  locale,
}) {
  const handleClick = async () => {
    if (isSaveLoading || !Object.keys(uploadedFiles ?? {}).length) return;
    setIsSaveLoading(true);

    try {
      const uploadResult = await postUdFileUpload(
        project_id,
        user_id,
        uploadedFiles,
        locale,
      );

      showAlert({
        text: uploadResult.messageKey ? "" : uploadResult.message,
        messageKey: uploadResult.messageKey,
        messageParams: uploadResult.messageParams,
        success: Boolean(uploadResult.success),
        autoClose: true,
      });

      if (!uploadResult.success) {
        return;
      }

      setUploadedFiles({});
      setThumbnails([]);

      try {
        if (typeof fetchProjectTree === "function") {
          const treeResult = await fetchProjectTree(user_id, project_id);

          if (!treeResult?.success) {
            console.warn("[FILE-TREE-REFRESH-FAILED]", treeResult?.message);
          }
        }
      } catch (treeErr) {
        console.error("[FILE-TREE-REFRESH-ERROR]", treeErr);
      }

      try {
        await onUploadSuccess?.(uploadResult);
      } catch (successCallbackErr) {
        console.error("[UPLOAD-SUCCESS-CALLBACK-ERROR]", successCallbackErr);
      }
    } catch (err) {
      console.error("[UPLOAD-ERROR]", err);
      showAlert({
        textKey: "projectDetail.fileUpload.alerts.unknownError",
        text: t("projectDetail.fileUpload.alerts.unknownError"),
        success: false,
        autoClose: true,
      });
    } finally {
      setIsSaveLoading(false);
    }
  };

  return (
    <button
      type="button"
      title={t("projectDetail.fileUpload.buttons.save")}
      className="btn-bg-blue"
      onClick={handleClick}
      disabled={isSaveLoading || !Object.keys(uploadedFiles ?? {}).length}
    >
      {isSaveLoading
        ? t("projectDetail.fileUpload.buttons.saving")
        : t("projectDetail.fileUpload.buttons.save")}
    </button>
  );
}
